import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { applyCouponAtCart, removeCoupon } from 'api/cart/_requests';
import { AxiosError } from 'axios';
import Button from 'components/base/Button';
import OrderSummaryDetails from 'components/common/OrderSummaryDetails';
import { currencyFormat } from 'helpers/utils';
import React from 'react';
import { Card, Form, FormControl, InputGroup } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { RootState } from 'redux-state/store';
import {
  applyCouponToVendor,
  removeCouponFromVendor
} from 'redux-state/user/cartSlice';

interface EcomCartSummaryCardProps {
  totalAmount: number;
  totalAmountAfterDiscount?: number;
  vendorId: string;
}

interface ApiErrorPayload {
  message: string;
}

const EcomCartSummaryCard = ({
  totalAmount,
  totalAmountAfterDiscount,
  vendorId
}: EcomCartSummaryCardProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [voucher, setVoucher] = React.useState('');

  const coupon = useSelector(
    (state: RootState) =>
      state.user.cart.cart.find(c => c.vendorId === vendorId)?.coupon
  );
  const handleApplyCoupon = async () => {
    try {
      const payload = {
        vendorId,
        code: voucher.trim()
      };
      const res = await applyCouponAtCart(payload);

      if (res.alreadyApplied) {
        toast.error(`Coupon ${res.code} is already applied`);
      } else {
        dispatch(
          applyCouponToVendor({
            vendorId,
            code: res.code,
            discount: res.discountAmount
          })
        );
        toast.success(`Coupon ${res.code} applied`);
      }
    } catch (err) {
      const axiosErr = err as AxiosError<ApiErrorPayload>;
      const msg =
        axiosErr.response?.data.message ??
        axiosErr.message ?? // network / timeout
        'Something went wrong'; // final fallback
      toast.error(msg);
    }
  };

  const handleProceed = async () => {
    const res = await removeCoupon({ vendorId });
    dispatch(removeCouponFromVendor({ vendorId }));
  };

  const navigateToCart = (vendorId: string) => {
    navigate(`/checkout/${vendorId}`);
  };

  return (
    <Card>
      <Card.Body>
        <div className="d-flex flex-between-center mb-3">
          <h3 className="mb-0">Summary</h3>
          <Link to="/cart" className="btn btn-link p-0">
            Edit cart
          </Link>
        </div>

        <Form.Select className="mb-3">
          <option value="cod">Cash on Delivery</option>
          <option value="card">Card</option>
          <option value="paypal">Paypal</option>
        </Form.Select>

        <OrderSummaryDetails
          totalAmount={totalAmount}
          discountAmount={coupon?.discountAmount || 0}
          shippingCost={0}
          taxAmount={0}
        />

        <InputGroup className="mb-3">
          <FormControl
            placeholder="Voucher"
            value={voucher}
            onChange={e => setVoucher(e.target.value)}
          />
          <Button
            variant="phoenix-primary"
            className="px-5"
            onClick={handleApplyCoupon}
          >
            Apply
          </Button>
        </InputGroup>

        {coupon && (
          <div className="d-flex justify-content-between mb-2">
            <span className="text-success fw-semibold">{coupon.code}</span>
            <span className="text-success">
              ‑{currencyFormat(coupon.discountAmount, { currency: 'SAR' })}
            </span>
          </div>
        )}

        <div className="d-flex justify-content-between border-y border-dashed border-translucent py-3 mb-4">
          <h4 className="mb-0">Total :</h4>
          <h4 className="mb-0">
            {currencyFormat(totalAmountAfterDiscount ?? totalAmount, {
              currency: 'SAR'
            })}
          </h4>
        </div>

        <Button
          className="w-100"
          variant="primary"
          onClick={() => navigateToCart(vendorId)}
          endIcon={
            <FontAwesomeIcon icon={faChevronRight} className="ms-1 fs-10" />
          }
        >
          Proceed to check out
        </Button>
      </Card.Body>
    </Card>
  );
};

export default EcomCartSummaryCard;
